import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
    Video,
    Download,
    Trash2,
    Play,
    Search,
    Clock,
    HardDrive,
    MoreVertical,
    FileVideo
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Recordings = () => {
    const [recordings, setRecordings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchRecordings();
    }, []);

    const fetchRecordings = async () => {
        try {
            const res = await axios.get(`${API}/recordings`);
            setRecordings(res.data);
        } catch (error) {
            console.error('Error fetching recordings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (recId) => {
        try {
            await axios.delete(`${API}/recordings/${recId}`);
            toast.success('Recording deleted');
            fetchRecordings();
        } catch (error) {
            console.error('Error deleting recording:', error);
            toast.error('Failed to delete recording');
        }
    };

    const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    const formatFileSize = (mb) => {
        if (mb >= 1000) {
            return `${(mb / 1000).toFixed(1)} GB`;
        }
        return `${mb.toFixed(0)} MB`;
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const filteredRecordings = recordings.filter(rec =>
        rec.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const totalSize = recordings.reduce((sum, rec) => sum + rec.file_size_mb, 0);

    return (
        <div className="space-y-6" data-testid="recordings-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold">Recordings</h1>
                    <p className="text-muted-foreground mt-1">
                        {recordings.length} recordings • {formatFileSize(totalSize)} total
                    </p>
                </div>
                <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Search recordings..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9"
                        data-testid="search-recordings"
                    />
                </div>
            </div>

            {/* Recordings Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Card key={i} className="animate-pulse">
                            <div className="aspect-video bg-muted" />
                            <CardContent className="p-4">
                                <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                                <div className="h-3 bg-muted rounded w-1/2" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : filteredRecordings.length === 0 ? (
                <div className="text-center py-16">
                    <FileVideo className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No recordings found</h3>
                    <p className="text-muted-foreground">
                        {searchQuery ? 'Try a different search term' : 'Your stream recordings will appear here'}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredRecordings.map((recording) => (
                        <Card
                            key={recording.id}
                            className="overflow-hidden group cursor-pointer hover:shadow-lg transition-all"
                            data-testid={`recording-${recording.id}`}
                        >
                            {/* Thumbnail */}
                            <div className="relative aspect-video bg-muted">
                                {recording.thumbnail_url ? (
                                    <img
                                        src={recording.thumbnail_url}
                                        alt={recording.title}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <Video className="w-12 h-12 text-muted-foreground" />
                                    </div>
                                )}
                                
                                {/* Play overlay */}
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                                        <Play className="w-6 h-6 text-gray-900 ml-1" />
                                    </div>
                                </div>

                                {/* Duration badge */}
                                <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
                                    {formatDuration(recording.duration_seconds)}
                                </div>

                                {/* Status badge */}
                                {recording.status === 'processing' && (
                                    <div className="absolute top-2 left-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded">
                                        Processing...
                                    </div>
                                )}
                            </div>

                            {/* Info */}
                            <CardContent className="p-4">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <h3 className="font-semibold truncate">{recording.title}</h3>
                                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {formatDate(recording.created_at)}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <HardDrive className="w-3 h-3" />
                                                {formatFileSize(recording.file_size_mb)}
                                            </span>
                                        </div>
                                    </div>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8">
                                                <MoreVertical className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem disabled={recording.status !== 'ready'}>
                                                <Download className="w-4 h-4 mr-2" />
                                                Download
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                className="text-destructive"
                                                onClick={() => handleDelete(recording.id)}
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Recordings;
